/* eslint-disable fsecond/valid-event-listener */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/no-shadow */
import { AnimatePresence } from 'framer-motion'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getCookie } from 'react-use-cookie'
import { isEmpty } from 'remeda'
import useResizeObserver from 'use-resize-observer'
import { useHover, useMediaQuery } from 'usehooks-ts'
import { Minisearch } from '@components/Minisearch/Minisearch'
import { useWishlist } from '@components/WishlistProvider/WishlistProvider'
import { dataTestIds, promotionalBannerVariants } from '@velasca/constants'
import { useCart } from '@velasca/hooks/use-cart'
import { useCountry } from '@velasca/hooks/use-country'
import { useHeader } from '@velasca/hooks/use-header'
import { useScrollLock } from '@velasca/hooks/use-scroll-lock'
import { useTop } from '@velasca/hooks/use-top'
import { AccessibilityIcon } from '@velasca/icons/accessibility'
import { AccountIcon } from '@velasca/icons/account'
import { AccountBlackFilledIcon } from '@velasca/icons/account-black-filled'
import { AccountWhiteFilledIcon } from '@velasca/icons/account-white-filled'
import { CartIcon } from '@velasca/icons/cart'
import { CartBlackFilledIcon } from '@velasca/icons/cart-black-filled'
import { SearchIcon } from '@velasca/icons/search'
import { WishlistBlackIcon } from '@velasca/icons/wishlist-black'
import { WishlistBlackFilledIcon } from '@velasca/icons/wishlist-black-filled'
import { WishlistWhiteIcon } from '@velasca/icons/wishlist-white'
import { WishlistWhiteFilledIcon } from '@velasca/icons/wishlist-white-filled'
import type { MenuItem, Navbar } from '@velasca/types'
import { ConditionalLink } from '@velasca/ui/conditional-link'
import { IconButton } from '@velasca/ui/icon-button'
import { ImageSkeleton } from '@velasca/ui/image-skeleton'
import { buildWidthSources } from '@velasca/utils/build-width-sources'
import { formatMenuTitle } from '@velasca/utils/format-menu-title'
import { $settings } from '@velasca/utils/globals'
import { handleError } from '@velasca/utils/handle-error'
import PromotionalBanner from '../PromotionalBanner'
import * as S from './styles'

const DesktopNavigation = ({
  menu,
  promotionalBanner,
  className = '',
  miscHeaderData = {},
  minisearch = {},
  sticky = false,
  reduced = false,
}: Navbar) => {
  const {
    currentTemplate,
    searchEnabled,
    switchCountryPage,
    sizePage,
    phoneNumber,
    publisherMenuImages,
    currency,
    whatsappUrl,
  } = miscHeaderData

  const { t } = useTranslation('header')

  const {
    isActive: isWishlistActive,
    setIsDrawerOpen: setWishlistDrawerOpen,
    itemsCount: wishlistItemsCount,
  } = useWishlist()

  const isMinWidth1400px = useMediaQuery('(min-width: 1400px)')

  // An object containing current hover menu entry indexes
  const [activeIndexes, setActiveIndexes] = useState<{
    firstLevelIndex: number
    columnIndex?: number
    secondLevelIndex?: number
    thirdLevelIndex?: number
  } | null>(null)

  const isMenuOpen = useMemo(() => {
    return !!activeIndexes
  }, [activeIndexes])

  const top = useTop()
  const [hover, setHover] = useState(false)
  const [searchActivated, setSearchActivated] = useState(false)
  const [searchOpened, setSearchOpened] = useState(false)
  const [searchTop, setSearchTop] = useState(0)
  const [searchHeightDelta, setSearchHeightDelta] = useState(0)

  // Use scroll lock hook to prevent layout shift
  useScrollLock(searchActivated)

  const newsletterCookie = getCookie('newsletter')

  const firstLevelIndex = useMemo(
    () => activeIndexes?.firstLevelIndex,
    [activeIndexes],
  )

  const currentMenu = useMemo(() => {
    if (menu && typeof firstLevelIndex !== 'undefined') {
      return menu[firstLevelIndex]
    }

    return null
  }, [menu, firstLevelIndex])

  const isInsideDirectLink = useMemo(() => {
    return Boolean(!currentMenu?.children && currentMenu?.url)
  }, [currentMenu])

  const currentMenuEntry = useMemo(() => {
    if (!activeIndexes || !currentMenu?.cols) {
      return null
    }

    const { columnIndex, secondLevelIndex, thirdLevelIndex } = activeIndexes

    if (
      typeof columnIndex === 'undefined' ||
      typeof secondLevelIndex === 'undefined'
    ) {
      return null
    }

    const secondLevel = currentMenu.cols[columnIndex][secondLevelIndex]

    if (secondLevel?.children && typeof thirdLevelIndex !== 'undefined') {
      return secondLevel.children[thirdLevelIndex]
    }

    if (typeof thirdLevelIndex === 'undefined') {
      return secondLevel
    }

    return null
  }, [activeIndexes, currentMenu])

  const promotionalBannerRef = useRef<HTMLDivElement>(null)
  const extendedLogoRef = useRef<HTMLDivElement>(null)
  const helpMenuRef = useRef<HTMLDivElement>(null)
  const navbarRef = useRef<HTMLUListElement>(null)
  const smallLogoRef = useRef<SVGSVGElement>(null)
  const accountButtonRef = useRef<HTMLDivElement>(null)
  const accountButtonHover = useHover(accountButtonRef)
  const wishlistButtonRef = useRef<HTMLDivElement>(null)
  const wishlistButtonHover = useHover(wishlistButtonRef)
  const cartButtonRef = useRef<HTMLDivElement>(null)
  const cartButtonHover = useHover(cartButtonRef)

  const { ref: firstLevelStartChunkRef } = useResizeObserver<HTMLDivElement>({
    box: 'border-box',
  })
  const { ref: firstLevelCenterChunkRef } = useResizeObserver<HTMLUListElement>(
    { box: 'border-box' },
  )
  const { ref: firstLevelEndChunkRef } = useResizeObserver<HTMLDivElement>({
    box: 'border-box',
  })

  const secondLevelCenterChunkRef = useRef<HTMLDivElement>(null)

  const setPromotionalBannerHeight = useHeader(
    (s) => s.setPromotionalBannerHeight,
  )
  const setHelpMenuHeight = useHeader((s) => s.setHelpMenuHeight)
  const setNavbarHeight = useHeader((s) => s.setNavbarHeight)

  useEffect(() => {
    const { height = 0 } = helpMenuRef?.current?.getBoundingClientRect() || {}

    setHelpMenuHeight(height)
  }, [setHelpMenuHeight])

  useEffect(() => {
    const { height = 0 } =
      promotionalBannerRef?.current?.getBoundingClientRect() || {}

    setPromotionalBannerHeight(height)
  }, [setPromotionalBannerHeight])

  useEffect(() => {
    const { height = 0 } = navbarRef?.current?.getBoundingClientRect() || {}

    setNavbarHeight(height)
  }, [setNavbarHeight])

  const helpMenuHeight = useHeader((s) => s.helpMenuHeight)
  const headerHeight = useHeader((s) => s.headerHeight)

  // Serve per settare il padding top del body
  useEffect(() => {
    if (sticky) {
      document.body.style.paddingTop = `${headerHeight}px`
    }
  }, [sticky, headerHeight])

  // Serve per gestire il "finto scroll" delle parti dell'header
  useEffect(() => {
    if (reduced) {
      return
    }

    const listener = () => {
      if (helpMenuRef?.current && navbarRef?.current) {
        helpMenuRef.current.style.position = `absolute`

        helpMenuRef.current.style.top = `0`
        helpMenuRef.current.style.left = `0`
        helpMenuRef.current.style.right = `0`

        navbarRef.current.style.position = `absolute`
        navbarRef.current.style.top = `${helpMenuHeight}px`
        navbarRef.current.style.left = `0`
        navbarRef.current.style.right = `0`

        if (window.scrollY >= 0) {
          helpMenuRef.current.style.top = `${-window.scrollY}px`

          if (window.scrollY > helpMenuHeight) {
            navbarRef.current.style.top = `0`
            if (smallLogoRef.current) {
              smallLogoRef.current.style.opacity = `1`
            }
          } else {
            navbarRef.current.style.top = `${helpMenuHeight - window.scrollY}px`

            if (smallLogoRef.current) {
              smallLogoRef.current.style.opacity = `0`
            }
          }
        }
      }
    }

    // La eseguo anche al mount perché non so qual è la posizione dello scroll
    // (potrebbe esserci un mount con scroll a metà pagina)
    listener()

    window.addEventListener('scroll', listener, { passive: true })

    return () => {
      window.removeEventListener('scroll', listener)
    }
  }, [helpMenuHeight, reduced])

  // Track if search was previously activated to avoid running animation on mount
  const prevSearchActivatedRef = useRef(searchActivated)

  useEffect(() => {
    const promotionalBannerHeight =
      promotionalBannerRef.current?.getBoundingClientRect().height ?? 0

    const navbarHeight = navbarRef.current?.getBoundingClientRect().height ?? 0

    const mustReduce = window.scrollY <= helpMenuHeight || reduced

    // Only run animation if searchActivated state actually changed
    const searchActivatedChanged =
      prevSearchActivatedRef.current !== searchActivated

    prevSearchActivatedRef.current = searchActivated

    if (searchActivated) {
      setActiveIndexes(null)
      if (
        mustReduce &&
        helpMenuRef.current &&
        navbarRef.current &&
        smallLogoRef.current
      ) {
        helpMenuRef.current.style.transition =
          'top 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        navbarRef.current.style.transition =
          'top 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        smallLogoRef.current.style.transition =
          'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        helpMenuRef.current.style.top = `-${helpMenuHeight}px`
        navbarRef.current.style.top = '0'
        smallLogoRef.current.style.opacity = `1`
      }
      setTimeout(() => {
        setSearchTop(navbarHeight)
        setSearchHeightDelta(promotionalBannerHeight + navbarHeight)
        setSearchOpened(true)
      }, 300)
    } else if (searchActivatedChanged) {
      // Only run the closing animation if search was actually deactivated (not on mount)
      setSearchOpened(false)
      if (
        mustReduce &&
        helpMenuRef.current &&
        navbarRef.current &&
        smallLogoRef.current
      ) {
        // First move helpMenu down
        helpMenuRef.current.style.top = '0'

        // Then move navbar to its position after helpMenu is in place
        setTimeout(() => {
          if (navbarRef.current && smallLogoRef.current) {
            navbarRef.current.style.top = `${helpMenuHeight}px`
            smallLogoRef.current.style.opacity = '0'
          }
        }, 150) // Half the transition time

        setTimeout(() => {
          if (
            helpMenuRef.current &&
            navbarRef.current &&
            smallLogoRef.current
          ) {
            helpMenuRef.current.style.transition = ''
            navbarRef.current.style.transition = ''
            smallLogoRef.current.style.transition = ''
          }
        }, 500)

        if (window.scrollY > 0) {
          window.scrollTo(0, 0)
        }
      }
    }
  }, [
    helpMenuHeight,
    reduced,
    searchActivated,
    setHelpMenuHeight,
    setNavbarHeight,
  ])

  const { cartItemCount, setIsDrawerOpen } = useCart()
  const country = useCountry()
  const countrySelector = useMemo(() => {
    if (country) {
      return (
        <S.HelpLabel>
          {t('changeCountry')}{' '}
          <S.HelpLink href={switchCountryPage}>
            <strong>{country.label}</strong>
          </S.HelpLink>
        </S.HelpLabel>
      )
    }

    return (
      <S.HelpLink href={switchCountryPage}>{t('changeCountry')}</S.HelpLink>
    )
  }, [country, switchCountryPage, t])

  const banners = useMemo(() => {
    if (typeof firstLevelIndex === 'undefined') {
      return null
    }
    const publisherMenuImagesSelected = publisherMenuImages?.filter(
      (publisherMenuImage) => {
        return (
          Number(publisherMenuImage.number) === firstLevelIndex + 1 &&
          typeof activeIndexes?.secondLevelIndex === 'undefined' &&
          typeof activeIndexes?.thirdLevelIndex === 'undefined'
        )
      },
    )

    if (publisherMenuImagesSelected && !isEmpty(publisherMenuImagesSelected)) {
      const maxColsForTwoBanners = isMinWidth1400px ? 3 : 2
      const publisherMenuImagesToShow =
        currentMenu?.cols && currentMenu.cols.length > maxColsForTwoBanners
          ? publisherMenuImagesSelected.slice(0, 1)
          : publisherMenuImagesSelected.slice(0, 2)

      return publisherMenuImagesToShow.map((publisherMenuImage) => {
        return (
          <ConditionalLink
            CustomLink={S.ImageLink}
            href={publisherMenuImage.url}
            key={`${publisherMenuImage.title}-${publisherMenuImage.number}-${publisherMenuImage.src}`}
          >
            <>
              <S.ImageContainer>
                <Image
                  src={publisherMenuImage.src}
                  alt={publisherMenuImage?.alt || currentMenuEntry?.title || ''}
                />
              </S.ImageContainer>
              <S.ImageTitle>
                {publisherMenuImage.title || currentMenuEntry?.title}
              </S.ImageTitle>
            </>
          </ConditionalLink>
        )
      })
    }

    if (!currentMenu?.cols) {
      return null
    }

    const menuEntriesImagesToShow: MenuItem[] = []

    for (
      let columnIndex = 0;
      columnIndex < currentMenu.cols.length;
      columnIndex++
    ) {
      const col = currentMenu.cols[columnIndex]

      for (const [secondLevelIndex, secondLevelEntry] of col.entries()) {
        const isSecondLevelEntryVisible =
          activeIndexes?.columnIndex === columnIndex &&
          activeIndexes?.secondLevelIndex === secondLevelIndex &&
          typeof activeIndexes?.thirdLevelIndex === 'undefined'

        if (isSecondLevelEntryVisible) {
          menuEntriesImagesToShow.push(secondLevelEntry)
        } else if (
          secondLevelEntry.children &&
          !isEmpty(secondLevelEntry.children)
        ) {
          for (const [
            thirdLevelIndex,
            thirdLevelEntry,
          ] of secondLevelEntry.children.entries()) {
            const isThirdLevelEntryVisible =
              activeIndexes?.columnIndex === columnIndex &&
              activeIndexes?.secondLevelIndex === secondLevelIndex &&
              activeIndexes?.thirdLevelIndex === thirdLevelIndex

            if (isThirdLevelEntryVisible) {
              menuEntriesImagesToShow.push(thirdLevelEntry)
            }
          }
        }
      }
    }

    return menuEntriesImagesToShow.map((menuEntry) => {
      return (
        <ConditionalLink
          CustomLink={S.ImageLink}
          href={menuEntry.url}
          key={menuEntry.id}
        >
          <>
            {menuEntry?.image ? (
              <S.ImageContainer>
                <Image
                  src={menuEntry.image}
                  alt={menuEntry.title}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                  }}
                />
              </S.ImageContainer>
            ) : (
              <S.EmptyImage />
            )}
            <S.ImageTitle visible={!!menuEntry?.image}>
              {menuEntry.title}
            </S.ImageTitle>
          </>
        </ConditionalLink>
      )
    })
  }, [
    activeIndexes?.columnIndex,
    activeIndexes?.secondLevelIndex,
    activeIndexes?.thirdLevelIndex,
    currentMenu?.cols,
    currentMenuEntry?.title,
    firstLevelIndex,
    isMinWidth1400px,
    publisherMenuImages,
  ])

  // eslint-disable-next-line
  const handleAccessibilityToggle = () => {
    try {
      // @ts-expect-error
      toggleAccessEzWidgetManually() // eslint-disable-line
    } catch (error) {
      handleError(error, { message: 'AccessEz widget not working.' })
    }
  }

  return (
    <>
      <S.ScrollLockGlobalStyles />
      <S.Header
        data-testid="desktop-navigation"
        className={className}
        isOpen={isMenuOpen}
        hover={hover}
        top={top}
        sticky={Boolean(sticky)}
        searchActivated={searchActivated}
        onMouseEnter={() => {
          if (sticky) {
            return
          }
          setHover(true)
        }}
        onMouseLeave={() => {
          if (!isMenuOpen || isInsideDirectLink) {
            setHover(false)
          }
          setActiveIndexes(null)
        }}
      >
        {promotionalBanner !== undefined && (
          <PromotionalBanner
            ref={promotionalBannerRef}
            {...promotionalBanner}
            variant={
              top && !isMenuOpen && !hover && !sticky && !searchActivated
                ? promotionalBannerVariants.SEMI_TRANSPARENT
                : promotionalBannerVariants.PRIMARY
            }
          />
        )}
        <S.Background>
          {!reduced && (
            <S.HelpMenu
              ref={helpMenuRef}
              isOpen={isMenuOpen}
              hover={hover}
              top={top}
              sticky={Boolean(sticky)}
              searchActivated={searchActivated}
            >
              <S.HelpMenuContent>
                <S.StartHelpChunk>
                  {countrySelector}
                  {currency?.supported && (
                    <>
                      <S.SpaceSeparator />
                      <S.CurrencySelector>
                        <S.HelpLabel>
                          {t('changeCurrency')}
                          <S.Select currencies={currency?.supported} />
                        </S.HelpLabel>
                      </S.CurrencySelector>
                    </>
                  )}
                </S.StartHelpChunk>
                {!reduced && (
                  <S.CenterHelpChunk ref={extendedLogoRef}>
                    {currentTemplate === 'index' ? (
                      <h1>
                        <S.StyledBrandLogoExtended
                          variant={
                            isMenuOpen || hover || !top || sticky
                              ? 'primary'
                              : 'white'
                          }
                        />
                      </h1>
                    ) : (
                      <S.Link href="/" title="Velasca">
                        <S.StyledBrandLogoExtended
                          variant={
                            isMenuOpen || hover || !top || sticky
                              ? 'primary'
                              : 'white'
                          }
                        />
                      </S.Link>
                    )}
                  </S.CenterHelpChunk>
                )}
                <S.EndHelpChunk>
                  {!newsletterCookie && t('newsletter') && (
                    <>
                      {sizePage && <S.Separator />}
                      <S.HelpLink
                        onClick={() => {
                          window.showNLBanner(true)
                        }}
                      >
                        {t('newsletter')}
                      </S.HelpLink>
                      <S.Separator />
                    </>
                  )}
                  {sizePage && t('size') && (
                    <>
                      <S.HelpLink href={sizePage}>{t('size')}</S.HelpLink>
                      <S.Separator />
                    </>
                  )}
                  {phoneNumber ? (
                    <>
                      <S.HelpLink href={`tel:${phoneNumber}`}>{`${t(
                        'phone',
                      )} ${phoneNumber}`}</S.HelpLink>
                      <S.Separator />
                    </>
                  ) : null}
                  {whatsappUrl ? (
                    <S.HelpLink href={whatsappUrl}>WhatsApp</S.HelpLink>
                  ) : null}
                </S.EndHelpChunk>
              </S.HelpMenuContent>
            </S.HelpMenu>
          )}
          <S.Navbar
            isOpen={isMenuOpen}
            hover={hover}
            top={top}
            sticky={Boolean(sticky)}
            searchActivated={searchActivated}
            ref={navbarRef}
          >
            <S.MenuBackground>
              <S.NavHead>
                <S.StartChunk ref={firstLevelStartChunkRef}>
                  <S.LogoLink href="/">
                    <S.StyledBrandLogoShort
                      ref={smallLogoRef}
                      $alwaysShow={reduced}
                    />
                  </S.LogoLink>
                  {$settings.accessibly_enabled ? (
                    <S.AccessibilityButton
                      className="accessibility-icon"
                      variant={
                        isMenuOpen || hover || !top || sticky || searchActivated
                          ? 'primary'
                          : 'secondary'
                      }
                      onClick={handleAccessibilityToggle}
                    >
                      <AccessibilityIcon />
                    </S.AccessibilityButton>
                  ) : null}
                  {searchEnabled && (
                    <IconButton
                      className="search-btn"
                      variant={
                        isMenuOpen || hover || !top || sticky || searchActivated
                          ? 'primary'
                          : 'secondary'
                      }
                      onClick={() => {
                        setSearchActivated((status) => !status)
                      }}
                    >
                      <SearchIcon />
                    </IconButton>
                  )}
                </S.StartChunk>
                <S.CenterChunk ref={firstLevelCenterChunkRef}>
                  {menu?.map((firstLevel, firstLevelIndex) => {
                    const { sanitizedTitle, className } = formatMenuTitle(
                      firstLevel.title,
                    )

                    return (
                      <li
                        key={
                          firstLevel.id ??
                          `${firstLevel.handle}-${firstLevelIndex}`
                        }
                      >
                        <ConditionalLink
                          href={firstLevel.url}
                          condition={Boolean(firstLevel?.url)}
                          CustomLink={S.NavLink}
                          target={firstLevel.isTargetBlank ? '_blank' : '_self'}
                        >
                          <S.FirstLevelMenuTitle
                            className={className}
                            title={sanitizedTitle}
                            isSelected={
                              firstLevelIndex === activeIndexes?.firstLevelIndex
                            }
                            onMouseEnter={() => {
                              setActiveIndexes({ firstLevelIndex })
                            }}
                          >
                            {sanitizedTitle}
                          </S.FirstLevelMenuTitle>
                        </ConditionalLink>
                      </li>
                    )
                  })}
                </S.CenterChunk>
                <S.EndChunk ref={firstLevelEndChunkRef}>
                  <div ref={wishlistButtonRef}>
                    {isWishlistActive && (
                      <IconButton
                        dot={wishlistItemsCount > 0}
                        filled={false}
                        variant={
                          isMenuOpen ||
                          hover ||
                          !top ||
                          sticky ||
                          searchActivated
                            ? 'primary'
                            : 'secondary'
                        }
                        onClick={() => {
                          setWishlistDrawerOpen((status) => !status)
                        }}
                      >
                        {wishlistButtonHover ? (
                          isMenuOpen ||
                          hover ||
                          !top ||
                          sticky ||
                          searchActivated ? (
                            <WishlistBlackFilledIcon />
                          ) : (
                            <WishlistWhiteFilledIcon />
                          )
                        ) : isMenuOpen ||
                          hover ||
                          !top ||
                          sticky ||
                          searchActivated ? (
                          <WishlistBlackIcon />
                        ) : (
                          <WishlistWhiteIcon />
                        )}
                      </IconButton>
                    )}
                  </div>
                  <div ref={accountButtonRef}>
                    <IconButton
                      link="/account"
                      variant={
                        isMenuOpen || hover || !top || sticky || searchActivated
                          ? 'primary'
                          : 'secondary'
                      }
                    >
                      {accountButtonHover ? (
                        isMenuOpen ||
                        hover ||
                        !top ||
                        sticky ||
                        searchActivated ? (
                          <AccountBlackFilledIcon />
                        ) : (
                          <AccountWhiteFilledIcon />
                        )
                      ) : (
                        <AccountIcon />
                      )}
                    </IconButton>
                  </div>
                  <div ref={cartButtonRef}>
                    <IconButton
                      dot={cartItemCount > 0}
                      variant={
                        isMenuOpen || hover || !top || sticky || searchActivated
                          ? 'primary'
                          : 'secondary'
                      }
                      onClick={() => {
                        setIsDrawerOpen(true)
                      }}
                    >
                      {cartButtonHover ? (
                        <CartBlackFilledIcon
                          data-testid={dataTestIds.CART_ICON}
                        />
                      ) : (
                        <CartIcon data-testid={dataTestIds.CART_ICON} />
                      )}
                    </IconButton>
                  </div>
                </S.EndChunk>
              </S.NavHead>
              <AnimatePresence
                onExitComplete={() => {
                  !isInsideDirectLink && setHover(false)
                }}
              >
                {currentMenu?.children && (
                  <S.MenuLinksBackground
                    isOpen={isMenuOpen}
                    hover={hover}
                    top={top}
                    sticky={Boolean(sticky)}
                    searchActivated={searchActivated}
                    initial={{ height: 0 }}
                    animate={{ height: 'auto' }}
                    exit={{ height: 0 }}
                    transition={{ type: 'spring', bounce: 0, duration: 0.3 }}
                    data-testid={dataTestIds.MENU_LINKS}
                  >
                    <S.MenuLinks>
                      <S.CenterMenuLinksContainer
                        ref={secondLevelCenterChunkRef}
                      >
                        {currentMenu?.cols?.map((col, i) => {
                          if (isEmpty(col)) {
                            return null
                          }

                          return (
                            <S.MenuColumn key={`menu-links-col${i}`}>
                              {col.map((secondLevel, j) => {
                                const {
                                  sanitizedTitle: secondLevelTitle,
                                  className: secondLevelClassName,
                                } = formatMenuTitle(secondLevel.title)

                                return (
                                  <S.SecondLevelGroup
                                    key={
                                      secondLevel.id ??
                                      `${secondLevel.handle}-${j}`
                                    }
                                  >
                                    <S.SecondLevelMenuTitle
                                      onMouseEnter={() => {
                                        if (activeIndexes) {
                                          setActiveIndexes({
                                            firstLevelIndex:
                                              activeIndexes.firstLevelIndex,
                                            columnIndex: i,
                                            secondLevelIndex: j,
                                          })
                                        }
                                      }}
                                    >
                                      <ConditionalLink
                                        href={secondLevel.url}
                                        className={secondLevelClassName}
                                        CustomLink={S.NavLink}
                                        target={
                                          secondLevel.isTargetBlank
                                            ? '_blank'
                                            : '_self'
                                        }
                                      >
                                        {secondLevelTitle}
                                      </ConditionalLink>
                                    </S.SecondLevelMenuTitle>
                                    {secondLevel.children &&
                                      !isEmpty(secondLevel.children) && (
                                        <S.SecondLevelMenuChildren>
                                          {secondLevel.children?.map(
                                            (thirdLevel, k) => {
                                              return (
                                                <S.ThirdLevelMenuTitle
                                                  key={
                                                    thirdLevel.id ??
                                                    `${thirdLevel.handle}-${k}`
                                                  }
                                                  onMouseEnter={() => {
                                                    if (activeIndexes) {
                                                      setActiveIndexes({
                                                        firstLevelIndex:
                                                          activeIndexes.firstLevelIndex,
                                                        columnIndex: i,
                                                        secondLevelIndex: j,
                                                        thirdLevelIndex: k,
                                                      })
                                                    }
                                                  }}
                                                >
                                                  <ConditionalLink
                                                    href={thirdLevel.url}
                                                    CustomLink={S.NavLink}
                                                    target={
                                                      thirdLevel.isTargetBlank
                                                        ? '_blank'
                                                        : '_self'
                                                    }
                                                  >
                                                    {thirdLevel.title}
                                                  </ConditionalLink>
                                                </S.ThirdLevelMenuTitle>
                                              )
                                            },
                                          )}
                                          {!isEmpty(secondLevel.children) &&
                                            !!secondLevel.url &&
                                            secondLevel.url !== '#' && (
                                              <S.ThirdLevelMenuTitle
                                                viewAll
                                                onMouseEnter={() => {
                                                  if (activeIndexes) {
                                                    setActiveIndexes({
                                                      firstLevelIndex:
                                                        activeIndexes.firstLevelIndex,
                                                      columnIndex: i,
                                                      secondLevelIndex: j,
                                                    })
                                                  }
                                                }}
                                              >
                                                <ConditionalLink
                                                  href={secondLevel.url}
                                                  CustomLink={S.NavLink}
                                                  target={
                                                    secondLevel.isTargetBlank
                                                      ? '_blank'
                                                      : '_self'
                                                  }
                                                >
                                                  {t('desktopViewAll')}
                                                </ConditionalLink>
                                              </S.ThirdLevelMenuTitle>
                                            )}
                                        </S.SecondLevelMenuChildren>
                                      )}
                                  </S.SecondLevelGroup>
                                )
                              })}
                            </S.MenuColumn>
                          )
                        })}
                        <S.MediaColumn>
                          <ImagesWrapper key={JSON.stringify(activeIndexes)}>
                            {banners}
                          </ImagesWrapper>
                        </S.MediaColumn>
                      </S.CenterMenuLinksContainer>
                    </S.MenuLinks>
                  </S.MenuLinksBackground>
                )}
              </AnimatePresence>
            </S.MenuBackground>
          </S.Navbar>
          <Minisearch
            top={searchTop}
            heightDelta={searchHeightDelta}
            visible={searchOpened}
            data={minisearch}
            onClose={() => {
              setSearchActivated(false)
            }}
          />
        </S.Background>
      </S.Header>
    </>
  )
}

const ImagesWrapper = ({ children }: { children: ReactNode }) => {
  const imageContainer = useRef<HTMLDivElement>(null)
  const [contentLoaded, setContentLoaded] = useState(false)

  useEffect(() => {
    if (!imageContainer.current) {
      return
    }
    const images = imageContainer.current.getElementsByTagName('img')

    let imagesLoaded = 0
    const totalImages = images.length

    // If there are no images, make the container visible immediately
    if (totalImages === 0) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-initialize-state
      setContentLoaded(true)

      return
    }

    const imageLoaded = () => {
      imagesLoaded++
      if (imagesLoaded === totalImages) {
        setContentLoaded(true)
      }
    }

    for (let i = 0; i < totalImages; i++) {
      // Check if the image is already complete (e.g., cached)
      if (images[i].complete) {
        imageLoaded()
      } else {
        images[i].addEventListener('load', imageLoaded)
      }
    }
  }, [])

  return (
    <S.ImagesWrapper ref={imageContainer} contentLoaded={contentLoaded}>
      {children}
    </S.ImagesWrapper>
  )
}

interface ImageProps {
  src: string
  alt: string
  lazy?: boolean
  onError?: (e: any) => void
  onLoad?: (e: any) => void
}

const Image = ({ src, alt, lazy = false, onError, onLoad }: ImageProps) => {
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)

  if (!src) {
    return <S.EmptyImage />
  }

  const { sources, sizes } = buildWidthSources(
    src,
    [
      { maxWidth: 1280, imageWidth: 400 },
      { maxWidth: 1600, imageWidth: 500 },
    ],
    600,
  )

  const handleLoad = (e: any) => {
    setImageLoaded(true)
    onLoad?.(e)
  }

  const handleError = (e: any) => {
    setImageError(true)
    onError?.(e)
  }

  if (imageError) {
    return <S.EmptyImage />
  }

  return (
    <S.ImageWithSkeletonContainer>
      {!imageLoaded && <ImageSkeleton aspectRatio="4 / 5" />}
      <S.Image
        lqip={lazy}
        lazyload={lazy}
        key={src}
        imgSrc={src}
        alt={alt}
        sizes={sizes}
        sources={sources}
        style={{
          opacity: imageLoaded ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
        }}
        onError={handleError}
        onLoad={handleLoad}
      />
    </S.ImageWithSkeletonContainer>
  )
}

export default DesktopNavigation
